'use client'

import { motion, useScroll, useTransform } from 'motion/react'
import { useRef } from 'react'
import { Leaf, Sun, Droplets } from 'lucide-react'

export function AboutSustainability() {
    const containerRef = useRef<HTMLDivElement>(null)
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"]
    })

    const y = useTransform(scrollYProgress, [0, 1], [100, -100])

    const features = [
        {
            id: "eco-friendly",
            icon: Leaf,
            title: "Eco-Friendly Materials",
            description: "We rigorously select locally sourced and recycled materials to minimize our carbon footprint while maintaining aesthetic excellence."
        },
        {
            id: "energy-efficiency",
            icon: Sun,
            title: "Energy Efficiency",
            description: "Implementing state-of-the-art solar integration and smart building systems to reduce energy consumption significantly."
        },
        {
            id: "water-conservation",
            icon: Droplets,
            title: "Water Conservation",
            description: "Advanced rainwater harvesting and greywater recycling systems ensure responsible resource management across all developments."
        }
    ]

    return (
        <section ref={containerRef} className="relative py-24 overflow-hidden bg-[#F0F4EF]"> {/* Light Sage/Green Tint background */}

            {/* Parallax Background Element */}


            <div className="container px-4 mx-auto relative z-10">
                <div className="grid lg:grid-cols-2 gap-16 items-center">

                    {/* Visual Side */}
                    <div className="relative">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl"
                        >
                            <img
                                src="/images/about/sustainability.png"
                                alt="Sustainable Architecture"
                                className="object-cover w-full h-full"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                            <div className="absolute bottom-8 left-8 text-white max-w-xs">
                                <p className="text-sm font-light uppercase tracking-widest mb-2">Future Ready</p>
                                <p className="font-serif text-2xl italic">Designing for a greener tomorrow.</p>
                            </div>
                        </motion.div>

                        {/* Floating Badge */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.4, duration: 0.6 }}
                            className="absolute -bottom-6 -right-6 md:right-12 bg-white p-6 rounded-xl shadow-2xl max-w-[200px]"
                        >
                            <div className="text-4xl font-display text-primary mb-1">LEED</div>
                            <div className="text-sm text-muted-foreground">Gold Certified Projects Standard</div>
                        </motion.div>
                    </div>

                    {/* Content Side */}
                    <div className="space-y-10">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="space-y-8"
                        >
                            <div className="space-y-4">
                                <span className="text-xs font-bold tracking-[0.3em] text-secondary uppercase block">
                                    Eco-Friendly
                                </span>
                                <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[0.9] text-primary">
                                    Commitment to <br />
                                    <span className="italic text-primary/50">Sustainability</span>
                                </h2>
                            </div>
                            <p className="text-xl text-muted-foreground leading-relaxed">
                                We believe that luxury and responsibility go hand in hand. Our sustainable development philosophy integrates nature with architecture, creating living spaces that breathe.
                            </p>
                        </motion.div>

                        <div className="grid gap-8">
                            {features.map((feature, i) => (
                                <motion.div
                                    key={feature.id}
                                    initial={{ opacity: 0, x: 20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.2 * i }}
                                    className="flex gap-6 group"
                                >
                                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm group-hover:scale-110 transition-transform duration-300">
                                        <feature.icon className="w-6 h-6 text-[#4A6741]" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-primary mb-2">{feature.title}</h3>
                                        <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </section>
    )
}
